import { createFileRoute, Link } from "@tanstack/react-router";
import { freshnessMeta, webPageSchemaScript } from "@/lib/page-freshness";
import { FaqSection, faqSchemaScript, type FaqItem } from "@/components/landing/FaqSection";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { ParticleBackground } from "@/components/landing/ParticleBackground";
import { PageIntro } from "@/components/landing/PageIntro";
import { ClosingCTA } from "@/components/landing/ClosingCTA";
import { RelatedServices } from "@/components/landing/RelatedServices";
import phonesVideo from "@/assets/floating-phones.webm.asset.json";
import phonesPoster from "@/assets/floating-phones-poster.jpg.asset.json";

const PATH = "/php-vulnerability-squarespace";
const URL = `https://www.squaremigration.com${PATH}`;
const PUBLISHED = "2026-10-28T00:00:00+00:00";
const MODIFIED = "2026-10-28T00:00:00+00:00";
const TITLE = "php2shell PHP Vulnerability Explained, and Why Squarespace Is Worth a Look";
const DESCRIPTION =
  "What the October 2026 php2shell PHP vulnerabilities are, how they happened, what WordPress and PHP site owners should do now, and why a managed platform like Squarespace can reduce the risk.";

const SOURCES = {
  foundation: "https://thephp.foundation/blog/2026/10/21/todays-php-security-release-and-how-were-strengthening-php-security/",
  cisa: "https://www.cisa.gov/news-events/alerts/2026/10/21/critical-vulnerabilities-php-require-immediate-patching",
  certeu: "https://cert.europa.eu/publications/security-advisories/2026-018",
  enisa: "https://www.enisa.europa.eu/news/joint-statement-on-critical-php-vulnerabilities",
  oom: "https://github.com/php/php-src/security/advisories/GHSA-w8hw-w9fx-j79w",
  stale: "https://github.com/php/php-src/security/advisories/GHSA-6wmf-4rv5-p3rp",
};

const FAQS: FaqItem[] = [
  {
    question: "Is my WordPress site affected by php2shell?",
    answer:
      "If your site runs on PHP older than 8.5.12, 8.4.27, 8.3.36 or 8.2.35, it is running a vulnerable version. WordPress itself is not the bug, but it runs on PHP, so ask your host which PHP version your site uses and whether it has been patched.",
  },
  {
    question: "What should I do if I can't update PHP right away?",
    answer:
      "CISA advises taking unpatched PHP systems offline until they can be updated, especially sites on PHP 8.1 or earlier, which no longer receive fixes. A web application firewall may give some temporary protection, but it is not a replacement for patching.",
  },
  {
    question: "Does Squarespace use PHP?",
    answer:
      "Squarespace doesn't publish the details of its server software. What matters for site owners is that Squarespace is a fully hosted platform: its team manages the servers and security updates, so you don't install or patch PHP, plugins or themes yourself.",
  },
  {
    question: "Will I lose my Google rankings if I move to Squarespace?",
    answer:
      "Not if the migration is planned properly. We map every old URL to its new page with 301 redirects, carry over titles and descriptions, and resubmit your sitemap in Google Search Console so your existing traffic carries over.",
  },
  {
    question: "How long does a migration to Squarespace take?",
    answer:
      "It depends on how much content you have. A small business site is usually quick, while larger sites with blogs or stores take longer. We review your current site first and give you a clear plan.",
  },
];

const TIMELINE = [
  { date: "Oct 20, 2026", text: "PHP announces an upcoming security release for two critical vulnerabilities." },
  { date: "Oct 21, 2026", text: "Fixes ship in PHP 8.5.12, 8.4.27, 8.3.36 and 8.2.35. EU cybersecurity agencies issue a joint warning." },
  { date: "Oct 23, 2026", text: "A third, lower-risk issue affecting unusual setups is disclosed. Proof-of-concept attack code is circulating." },
  { date: "Oct 26, 2026", text: "CISA adds both vulnerabilities to its Known Exploited Vulnerabilities catalog, confirming real-world attacks." },
];

const CHECKLIST = [
  "Ask your host which PHP version your site runs and whether it is patched.",
  "Update to PHP 8.5.12, 8.4.27, 8.3.36 or 8.2.35 or later.",
  "If you are on PHP 8.1 or earlier, plan an upgrade now. Those versions no longer get fixes.",
  "Check other tools that may include PHP quietly, like control panels, forums or old landing pages.",
  "Turn on a web application firewall as a stopgap, not a fix.",
  "Watch for warning signs: unknown admin users, new files, unexpected redirects or a hosting suspension notice.",
];

const EXT = "text-primary hover:underline";

export const Route = createFileRoute("/php-vulnerability-squarespace")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: URL },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
      ...freshnessMeta({ published: PUBLISHED, modified: MODIFIED }),
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: TITLE,
          description: DESCRIPTION,
          url: URL,
          datePublished: PUBLISHED,
          dateModified: MODIFIED,
          author: { "@type": "Person", name: "Chris Gould" },
          publisher: { "@id": "https://www.squaremigration.com/#organization" },
          keywords: "php2shell, PHP vulnerability, CVE-2026-109618, CVE-2026-109619, WordPress security, Squarespace migration",
        }),
      },
      faqSchemaScript(FAQS),
      webPageSchemaScript({ path: PATH, name: TITLE, description: DESCRIPTION, published: PUBLISHED, modified: MODIFIED }),
    ],
  }),
  component: PhpArticlePage,
});

function Section({ eyebrow, title, children }: { eyebrow: string; title: string; children: React.ReactNode }) {
  return (
    <section className="px-5 sm:px-6 py-12 md:py-16">
      <div className="mx-auto max-w-4xl">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">{eyebrow}</p>
        <h2 className="mt-3 text-3xl md:text-4xl font-bold text-foreground">{title}</h2>
        <div className="mt-6 space-y-4 text-muted-foreground leading-relaxed text-lg">{children}</div>
      </div>
    </section>
  );
}

function PhpArticlePage() {
  return (
    <main className="relative">
      <ParticleBackground />
      <Header />
      <PageIntro
        eyebrow="Article: Website Security"
        title="The php2shell PHP vulnerability: what happened, and why Squarespace is worth a look."
        description="In October 2026, two critical flaws in PHP put millions of self-hosted websites at risk. Here is what they are, how they happened, what to do now, and why a managed platform can take this off your plate."
        bullets={["What it is", "How it happened", "What to do now", "Why Squarespace"]}
      />

      <Section eyebrow="Quick Summary" title="The short version">
        <div className="rounded-xl border border-primary/40 bg-primary/5 p-5 text-foreground space-y-3 text-base">
          <p><strong>What:</strong> Two critical PHP bugs, nicknamed "php2shell" (CVE-2026-109618 and CVE-2026-109619), let attackers run their own code on a web server without logging in.</p>
          <p><strong>Who:</strong> Any site running PHP below 8.5.12, 8.4.27, 8.3.36 or 8.2.35. That includes many WordPress, WooCommerce, Drupal and Joomla sites.</p>
          <p><strong>Now:</strong> Attacks are confirmed in the wild. Patch today, or ask your host to.</p>
        </div>
      </Section>

      <Section eyebrow="Background" title="What is PHP, and why does it matter?">
        <p>
          PHP is the programming language behind a huge share of the web. WordPress, WooCommerce, Drupal,
          Joomla and countless plugins and hosting control panels run on it. If you have a self-hosted
          website, there's a good chance PHP is working behind the scenes, even if you've never heard of it.
        </p>
        <p>
          Because it's everywhere, a serious PHP flaw affects far more than one product. European agencies
          noted that many tools{" "}
          <a href={SOURCES.enisa} target="_blank" rel="noopener noreferrer" className={EXT}>rely on PHP in the background</a>,
          so some businesses are exposed without realizing it.
        </p>
      </Section>

      <Section eyebrow="The Vulnerability" title="What is php2shell?">
        <p>
          On October 21, 2026, the PHP project{" "}
          <a href={SOURCES.foundation} target="_blank" rel="noopener noreferrer" className={EXT}>released fixes for two critical vulnerabilities</a>{" "}
          that, under the right conditions, let an unauthenticated attacker run code on a server. In plain
          terms: a stranger on the internet could take control of your website's server without a password.
        </p>
        <p>
          According to{" "}
          <a href={SOURCES.cisa} target="_blank" rel="noopener noreferrer" className={EXT}>CISA</a>, the flaws
          affect PHP installations that serve web pages, including Apache with mod_php (the highest-risk
          setup), PHP-FPM, php-cgi, FrankenPHP and LiteSpeed. That covers most common hosting setups.
        </p>
      </Section>

      <Section eyebrow="How It Happened" title="How it happened, in plain English">
        <p>Both bugs come from the way PHP cleans up after handling a visitor's request.</p>
        <h3 className="text-xl font-semibold text-foreground">1. Running out of memory at the worst moment</h3>
        <p>
          PHP limits how much memory each request can use. The{" "}
          <a href={SOURCES.oom} target="_blank" rel="noopener noreferrer" className={EXT}>first advisory</a>{" "}
          shows that a specially built form upload could push PHP past that limit while it was still setting
          things up. PHP would stop abruptly and leave half-built data behind, then treat it as valid. On
          Apache with mod_php, this could happen before your website's own code even ran, with default settings.
        </p>
        <h3 className="text-xl font-semibold text-foreground">2. Leftovers from the previous visitor</h3>
        <p>
          The{" "}
          <a href={SOURCES.stale} target="_blank" rel="noopener noreferrer" className={EXT}>second advisory</a>{" "}
          describes "stale" data. When an error interrupted one request, PHP could forget to clear some
          internal notes. The next request handled by the same server process could then read memory that
          had already been thrown away. An attacker who arranges that memory carefully can turn it into full
          control.
        </p>
        <p>
          Neither bug was caused by a website owner doing something wrong. They lived deep inside PHP itself,
          which is exactly why they're so widespread.
        </p>
      </Section>

      <Section eyebrow="Timeline" title="How it unfolded">
        <ol className="space-y-4 border-l border-primary/40 pl-6">
          {TIMELINE.map((t) => (
            <li key={t.date}>
              <p className="text-sm font-semibold text-primary">{t.date}</p>
              <p className="text-base">{t.text}</p>
            </li>
          ))}
        </ol>
        <p className="text-base">
          Sources:{" "}
          <a href={SOURCES.certeu} target="_blank" rel="noopener noreferrer" className={EXT}>CERT-EU</a>,{" "}
          <a href={SOURCES.cisa} target="_blank" rel="noopener noreferrer" className={EXT}>CISA</a>,{" "}
          <a href={SOURCES.enisa} target="_blank" rel="noopener noreferrer" className={EXT}>ENISA</a>.
        </p>
      </Section>

      <Section eyebrow="Action Steps" title="If you run WordPress or another PHP site">
        <ul className="list-disc space-y-2 pl-6">
          {CHECKLIST.map((c) => <li key={c}>{c}</li>)}
        </ul>
        <p>
          CISA's guidance is blunt: systems that can't be patched{" "}
          <a href={SOURCES.cisa} target="_blank" rel="noopener noreferrer" className={EXT}>should be taken offline</a>{" "}
          until they are.
        </p>
      </Section>

      <Section eyebrow="The Bigger Lesson" title="Self-hosting means you own the patching">
        <p>
          php2shell is a reminder of a quiet trade-off. When you run a self-hosted site, someone has to keep
          the server, the PHP version, the content system, every plugin and every theme up to date. For many
          small and mid-sized businesses, that someone is a busy marketing person, a freelancer who moved on,
          or nobody at all.
        </p>
        <p>
          Old PHP versions, abandoned plugins and forgotten staging sites are where attacks usually land. The
          fix for this month's bug is simple. Remembering to apply every fix, every month, for years, is not.
        </p>
      </Section>

      <Section eyebrow="An Alternative" title="Why Squarespace can be a good option">
        <div className="mx-auto w-full max-w-md">
          <video
            src={phonesVideo.url}
            poster={phonesPoster.url}
            autoPlay
            muted
            loop
            playsInline
            preload="none"
            aria-label="Squarespace site displayed on floating phones"
            className="w-full rounded-xl"
          />
        </div>
        <p>
          Squarespace is a fully hosted, managed platform. Instead of renting a server and maintaining
          software on it, you build on a platform whose own team handles the infrastructure. For a typical
          business website, that changes a lot:
        </p>
        <ul className="list-disc space-y-2 pl-6">
          <li><strong className="text-foreground">No server or PHP to patch.</strong> You never pick, install or upgrade a language version.</li>
          <li><strong className="text-foreground">No plugin sprawl.</strong> Core features are built in, so there's no pile of third-party add-ons to keep current.</li>
          <li><strong className="text-foreground">SSL and updates included.</strong> Security certificates and platform updates come with your plan.</li>
          <li><strong className="text-foreground">More time for marketing.</strong> Your team spends its hours on content and leads, not maintenance.</li>
        </ul>
        <p>
          To be fair, no platform is risk-free, and Squarespace isn't the right fit for everyone. You give up
          server access and some of the deep customization a self-hosted site allows. Account security, like
          strong passwords and two-factor login, is still your job. But for most marketing sites, the trade
          is worth it.
        </p>
      </Section>

      <Section eyebrow="Thinking About Switching?" title="Move to Squarespace without losing your rankings">
        <p>
          Square Migration started in 2021 doing exactly this: moving businesses from WordPress, Wix, Webflow
          and others onto Squarespace. We map every old URL with 301 redirects, carry over your content and
          SEO settings, and resubmit your sitemap so Google picks up the new site cleanly.
        </p>
        <p>
          <Link to="/squarespace-migrations" className={EXT}>See how our Squarespace migrations work</Link>{" "}
          or explore our{" "}
          <Link to="/squarespace-websites" className={EXT}>Squarespace website services</Link>.
        </p>
      </Section>

      <FaqSection items={FAQS} />
      <ClosingCTA
        eyebrow="Next Step"
        headline={<>Tired of patching <span className="text-primary">your website?</span></>}
        subhead="Let's talk about a simpler setup."
        body="Send us your current site and we'll review what you have, what should move over and how to protect your traffic."
        ctaLabel="Meet Virtually"
      />
      <RelatedServices current={PATH} />
      <Footer />
    </main>
  );
}
